import {
  AUTH_HOST,
  AUTH_USER,
  AUTH_CREDENTIALS,
  AUTH_SCOPES,
  USER_USER,
  USER_CREDENTIALS,
  USER_SCOPES
} from '../config/config'
import axios from 'axios'
import sha1 from 'sha1'
import { get, post } from './axiosService'

const TOKEN_URL = AUTH_HOST + '/oauth/token'
const CREATE_USER_URL = 'rest/api/v1/backAuth/createUser'
const GET_USER_DETAILS_URL = 'rest/api/v1/backAuth/getUser'
const REQUEST_PASSWORD_RESET_URL = 'rest/api/v1/backAuth/requestPasswordReset'
const ALLOW_PASSWORD_RESET_URL = 'auth/allowPasswordReset'
const CHANGE_PASSWORD_URL = 'rest/api/v1/backAuth/changePassword'

class UserService {

  static async init () {
    while (UserService.auth == null || UserService.auth.expires_at < new Date()) {
      UserService.auth = await oauth2Client.getTokenForClient(AUTH_USER, AUTH_CREDENTIALS, AUTH_SCOPES)
      if (UserService.auth.expires_in) {
        UserService.auth.expires_at = new Date(new Date().getTime() + UserService.auth.expires_in * 1000)
      }
    }
  }

  static async registerUser (activationCode, email, password) {
    try {
      await UserService.init()

      const data = {
        code: activationCode,
        email: email,
        nickname: email,
        password: sha1(password),
        agreed: 'true'
      }

      const token = UserService.auth.access_token
      let response = await post(`${AUTH_HOST}/${CREATE_USER_URL}`, data, token)
      // console.log('INFO','userService.registerUser','registerResponse', response)
      if (response.success !== true) {
        throw new Error(response.errors.join(','))
      }

      return Promise.resolve(response)
    }
    catch (error) {
      throw error
    }
  }

  static async login (email, password, scopes) {
    try {
      await UserService.init()

      let data = {
        nickname: email,
        email: email,
      }

      const token = UserService.auth.access_token
      let response = await post(`${AUTH_HOST}/${GET_USER_DETAILS_URL}`, data, token)
      // console.log('INFO','userService.login', 'userInfo', response)

      if (response.success === true && response.user.username) {
        let auth = await oauth2Client.getTokenForUser(response.user.username, password, scopes)
        if (auth.expires_in) {
          auth.expires_at = new Date(new Date().getTime() + auth.expires_in * 1000)
        }
        // console.log('INFO','userService.login', 'auth', auth)
        return auth
      } else {
        throw new Error(response.errors.join(','))
      }

    } catch (error) {
      throw error
    }
  }

  static async requestPasswordReset (email) {
    try {
      await UserService.init()

      let data = {
        nickname: email,
      }

      const token = UserService.auth.access_token
      let response = await post(`${AUTH_HOST}/${REQUEST_PASSWORD_RESET_URL}`, data, token)
      if (response.success === true && response.maxSeconds) {
        // console.log('INFO', 'userService.requestPasswordReset', 'resetRequest', response)
        return response
      } else {
        throw new Error(response.errors.join(','))
      }
    } catch (error) {
      throw error
    }
  }

  static async resetPassword (token, signature, password) {
    try {
      await UserService.init()

      let response = await get(`${AUTH_HOST}/${ALLOW_PASSWORD_RESET_URL}`, {token: token, signature: signature})
      // console.log('INFO', 'userService.resetPassword', 'allowRequest', typeof response, response)
      if (typeof response === 'string') {
        throw new Error(response.replace(/^[\s\S]*<p>([\s\S]*?)<\/p>[\s\S]*$/gm, '$1'))
      } else if (response.success === true && response.data && response.data.username) {
        const token = UserService.auth.access_token
        let data = {
          username: response.data.username,
          newpassword: sha1(password)
        }
        response = await post(`${AUTH_HOST}/${CHANGE_PASSWORD_URL}`, data, token)
        // console.log('INFO', 'userService.resetPassword', 'change Password', response)
        return response
      } else {
        throw new Error('Unbekannter Fehler')
      }
    } catch (error) {
      throw error
    }
  }
}

UserService.auth = null

class oauth2Client {
  static async getTokenForClient (clientId, clientSecret, scopes) {
    const userPwd = clientId + ':' + clientSecret

    const config = {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Authorization: 'Basic ' + window.btoa(userPwd)
      },
      timeout: 15000
    }

    let data = new window.FormData()
    if (scopes) data.append('scope', scopes)
    data.append('grant_type', 'client_credentials')

    try {
      let result = await axios.post(TOKEN_URL, data, config)
      if (result.status === 200) {
        return result.data
      } else {
        return Promise.reject(new Error('Wrong request for Token: ' + result.status))
      }
    } catch (reason) {
      return Promise.reject(new Error('Wrong request for Token: ' + reason.toString()))
    }
  }

  static async getTokenForUser (username, password, scopes) {
    const userPwd = USER_USER + ':' + USER_CREDENTIALS

    const config = {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Authorization: 'Basic ' + window.btoa(userPwd)
      },
      timeout: 15000
    }

    let data = new window.FormData()
    // data.append('client_id', AUTH_USER)
    data.append('grant_type', 'password')
    data.append('username', username)
    data.append('password', sha1(password))
    if (scopes) data.append('scope', scopes)

    try {
      let result = await axios.post(TOKEN_URL, data, config)
      if (result.status === 200) {
        return result.data
      } else {
        throw new Error('Wrong request for Token: ' + result.status)
      }
    } catch (reason) {
      if (reason.response && reason.response.data && reason.response.data.error_description) {
        throw new Error(reason.response.data.error_description)
      } else {
        throw reason
      }
    }
  }

  static async refreshAuth (token, scopes) {
    const userPwd = USER_USER + ':' + USER_CREDENTIALS
    const config = {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Authorization: 'Basic ' + window.btoa(userPwd)
      },
      timeout: 15000
    }

    let data = new window.FormData()
    data.append('grant_type', 'refresh_token')
    data.append('refresh_token', token)
    if (scopes) data.append('scope', scopes)

    try {
      let result = await axios.post(TOKEN_URL, data, config)
      if (result.status === 200) {
        return result.data
      } else {
        throw new Error('Wrong request for Token: ' + result.status)
      }
    } catch (reason) {
      if (reason.response && reason.response.data && reason.response.data.error_description) {
        throw new Error(reason.response.data.error_description)
      } else {
        throw reason
      }
    }
  }
}

export async function validateAuth (auth) {
  if (auth && auth.access_token) {
    if (auth.expires_at && auth.expires_at > new Date()) {
      return auth
    } else if (auth.refresh_token) {
      try {
        let newAuth = await oauth2Client.refreshAuth(auth.refresh_token, USER_SCOPES)
        if (newAuth.expires_in) {
          newAuth.expires_at = new Date(new Date().getTime() + newAuth.expires_in * 1000)
        }
        return newAuth
      } catch (error) {
        console.error('validate Auth', error)
        return null
      }
    }
  } else {
    return null
  }
}

export default UserService